export type Language = 'en' | 'sn' | 'nd' | 'sw';

export const SUPPORTED_LANGUAGES: { code: Language; label: string; nativeLabel: string; flag: string }[] = [
  { code: 'en', label: 'English', nativeLabel: 'English', flag: '🇬🇧' },
  { code: 'sn', label: 'Shona', nativeLabel: 'ChiShona', flag: '🇿🇼' },
  { code: 'nd', label: 'Ndebele', nativeLabel: 'IsiNdebele', flag: '🇿🇼' },
  { code: 'sw', label: 'Swahili', nativeLabel: 'Kiswahili', flag: '🌍' },
];

const translations: Record<Language, Record<string, string>> = {
  en: {
    // Nav
    'nav.theWall': 'The Wall',
    'nav.stories': 'Stories',
    'nav.archives': 'Archives',
    'nav.community': 'Community',
    'nav.startProject': 'Start a Project',
    'nav.guide': 'Guide',
    // Contributions list
    'contributions.title': 'Published Stories',
    'contributions.subtitle': 'Every story here has been reviewed and approved by a human curator. Certainty labels — Oral Tradition, Sourced Claim, Firsthand Account — are always shown so you know how to weigh each record.',
    'contributions.searchPlaceholder': 'Search stories…',
    'contributions.allEras': 'All Eras',
    'contributions.allTypes': 'All Types',
    'contributions.allReactions': 'All Reactions',
    'contributions.clearFilters': 'Clear',
    'contributions.noMatch': 'No stories match your filters',
    'contributions.noStories': 'No published stories yet',
    'contributions.noMatchDesc': "Try adjusting your search or filters to find what you're looking for.",
    'contributions.noStoriesDesc': 'Be the first to contribute to the archive. Every piece of history matters.',
    'contributions.submitCta': 'Submit a contribution',
    'contributions.signInCta': 'Sign in to contribute',
    'contributions.startProject': 'Start a Project',
    'contributions.communityArchive': 'Community Archive',
    'contributions.anonymous': 'Anonymous',
    // Submission form
    'submission.title': 'Submit a Contribution',
    'submission.titleLabel': 'Story Title',
    'submission.titlePlaceholder': 'Enter a clear, descriptive title',
    'submission.era': 'Historical Era',
    'submission.topic': 'Topic',
    'submission.region': 'Region',
    'submission.certainty': 'Certainty Label',
    'submission.body': 'Story Body',
    'submission.anonymous': 'Submit anonymously',
    'submission.saveDraft': 'Save Draft',
    'submission.submit': 'Submit for Review',
    'submission.submitting': 'Submitting…',
    'submission.language': 'Story Language',
    'submission.languageHint': 'Select the language your story is written in',
    // Certainty labels
    'certainty.oral_tradition': 'Oral Tradition',
    'certainty.sourced_claim': 'Sourced Claim',
    'certainty.firsthand_account': 'Firsthand Account',
    // Reading
    'reading.wordCount': 'words',
    'reading.by': 'By',
    'reading.shareContribution': 'Share this contribution',
  },
  sn: {
    // Nav
    'nav.theWall': 'Rusvingo',
    'nav.stories': 'Nyaya',
    'nav.archives': 'Zvinyorwa',
    'nav.community': 'Nharaunda',
    'nav.startProject': 'Tanga Chirongwa',
    'nav.guide': 'Gwara',
    // Contributions list
    'contributions.title': 'Nyaya Dzakaburitswa',
    'contributions.subtitle': 'Nyaya dzose pano dzakatarisiwa uye dzakabvumidzwa nemuchengetedzi wevanhu. Zviratidzo zvechokwadi — Tsika dzeKare, Chirevo Chine Gwaro, Umboo Hwemunhu — zvinoratidzwa nguva dzose kuti uzive mwero wekutora rekodhi rimwe nerimwe.',
    'contributions.searchPlaceholder': 'Tsvaga nyaya…',
    'contributions.allEras': 'Nguva Dzose',
    'contributions.allTypes': 'Mhando Dzose',
    'contributions.allReactions': 'Mhinduro Dzose',
    'contributions.clearFilters': 'Bvisa',
    'contributions.noMatch': 'Hapana nyaya dzinoenderana nezvawasarudza',
    'contributions.noStories': 'Hapana nyaya dzakaburitswa',
    'contributions.noMatchDesc': 'Edza kugadzirisa kutsvaga kwako kana zvausarudza kuti uwane zvaunoda.',
    'contributions.noStoriesDesc': 'Iva wekutanga kupinda muzvinyorwa. Chikamu chese chehistory chinokosha.',
    'contributions.submitCta': 'Tumira mupiro',
    'contributions.signInCta': 'Pinda kuti upire',
    'contributions.startProject': 'Tanga Chirongwa',
    'contributions.communityArchive': 'Zvinyorwa zveNharaunda',
    'contributions.anonymous': 'Pasina Zita',
    // Submission form
    'submission.title': 'Tumira Mupiro',
    'submission.titleLabel': 'Musoro weNyaya',
    'submission.titlePlaceholder': 'Nyora musoro wakajeka unotsanangura',
    'submission.era': 'Nguva yeHistory',
    'submission.topic': 'Musoro',
    'submission.region': 'Nzvimbo',
    'submission.certainty': 'Chiratidzo Chechokwadi',
    'submission.body': 'Muviri weNyaya',
    'submission.anonymous': 'Tumira pasina zita',
    'submission.saveDraft': 'Chengetedza Dhirafiti',
    'submission.submit': 'Tumira Kutariswa',
    'submission.submitting': 'Kutumira…',
    'submission.language': 'Mutauro weNyaya',
    'submission.languageHint': 'Sarudza mutauro wawakanyora nyaya yako',
    // Certainty labels
    'certainty.oral_tradition': 'Tsika dzeKare',
    'certainty.sourced_claim': 'Chirevo Chine Gwaro',
    'certainty.firsthand_account': 'Umboo Hwemunhu',
    // Reading
    'reading.wordCount': 'mazwi',
    'reading.by': 'Na',
    'reading.shareContribution': 'Govera mupiro uyu',
  },
  nd: {
    // Nav
    'nav.theWall': 'Udonga',
    'nav.stories': 'Izindaba',
    'nav.archives': 'Imibhalo',
    'nav.community': 'Umphakathi',
    'nav.startProject': 'Qala Iphrojekthi',
    'nav.guide': 'Isiqondiso',
    // Contributions list
    'contributions.title': 'Izindaba Ezikhishiwe',
    'contributions.subtitle': 'Zonke izindaba lapha zihlolwe futhi zivunywe ngumgcini wabantu. Izihloko zokuqiniseka — Amasiko Omlomo, Isicelo Esinemthombo, Ubufakazi Bomuntu — zihlala zikhona ukuze wazi ukuthi uzinike isigqi esingakanani.',
    'contributions.searchPlaceholder': 'Sesha izindaba…',
    'contributions.allEras': 'Zonke Izikhathi',
    'contributions.allTypes': 'Zonke Izinhlobo',
    'contributions.allReactions': 'Zonke Izimpendulo',
    'contributions.clearFilters': 'Sula',
    'contributions.noMatch': 'Azikho izindaba ezihambelana nokukhetha kwakho',
    'contributions.noStories': 'Azikho izindaba ezikhishiwe',
    'contributions.noMatchDesc': 'Zama ukulungisa ukusesha kwakho noma ukukhetha ukuze uthole ofuna khona.',
    'contributions.noStoriesDesc': 'Yiba owokuqala ukufaka emlanjeni. Ingxenye yonke yomlando ibalulekile.',
    'contributions.submitCta': 'Thumela umnikelo',
    'contributions.signInCta': 'Ngena ukuze unikele',
    'contributions.startProject': 'Qala Iphrojekthi',
    'contributions.communityArchive': 'Imibhalo Yomphakathi',
    'contributions.anonymous': 'Ngaphandle Kwegama',
    // Submission form
    'submission.title': 'Thumela Umnikelo',
    'submission.titleLabel': 'Isihloko Sendaba',
    'submission.titlePlaceholder': 'Faka isihloko esicacile esichazayo',
    'submission.era': 'Isikhathi Somlando',
    'submission.topic': 'Isihloko',
    'submission.region': 'Isifunda',
    'submission.certainty': 'Uphawu Lokuqiniseka',
    'submission.body': 'Umzimba Wendaba',
    'submission.anonymous': 'Thumela ngaphandle kwegama',
    'submission.saveDraft': 'Gcina Idrafti',
    'submission.submit': 'Thumela Ukuhlolwa',
    'submission.submitting': 'Iyathumela…',
    'submission.language': 'Ulimi Lwendaba',
    'submission.languageHint': 'Khetha ulimi owabhala ngalo indaba yakho',
    // Certainty labels
    'certainty.oral_tradition': 'Amasiko Omlomo',
    'certainty.sourced_claim': 'Isicelo Esinemthombo',
    'certainty.firsthand_account': 'Ubufakazi Bomuntu',
    // Reading
    'reading.wordCount': 'amagama',
    'reading.by': 'Ngu',
    'reading.shareContribution': 'Yabelana ngalomnikelo',
  },
  sw: {
    // Nav
    'nav.theWall': 'Ukuta',
    'nav.stories': 'Hadithi',
    'nav.archives': 'Kumbukumbu',
    'nav.community': 'Jamii',
    'nav.startProject': 'Anza Mradi',
    'nav.guide': 'Mwongozo',
    // Contributions list
    'contributions.title': 'Hadithi Zilizochapishwa',
    'contributions.subtitle': 'Kila hadithi hapa imekaguliwa na kupitishwa na msimamizi wa binadamu. Lebo za uhakika — Mila ya Mdomo, Dai Lenye Chanzo, Ushuhuda wa Moja kwa Moja — zinaonyeshwa daima ili ujue jinsi ya kupima kila rekodi.',
    'contributions.searchPlaceholder': 'Tafuta hadithi…',
    'contributions.allEras': 'Nyakati Zote',
    'contributions.allTypes': 'Aina Zote',
    'contributions.allReactions': 'Majibu Yote',
    'contributions.clearFilters': 'Futa',
    'contributions.noMatch': 'Hakuna hadithi zinazolingana na vichujio vyako',
    'contributions.noStories': 'Hakuna hadithi zilizochapishwa bado',
    'contributions.noMatchDesc': 'Jaribu kurekebisha utafutaji wako au vichujio kupata unachotafuta.',
    'contributions.noStoriesDesc': 'Kuwa wa kwanza kuchangia kwenye kumbukumbu. Kila kipande cha historia kina umuhimu.',
    'contributions.submitCta': 'Wasilisha mchango',
    'contributions.signInCta': 'Ingia ili kuchangia',
    'contributions.startProject': 'Anza Mradi',
    'contributions.communityArchive': 'Kumbukumbu ya Jamii',
    'contributions.anonymous': 'Bila Jina',
    // Submission form
    'submission.title': 'Wasilisha Mchango',
    'submission.titleLabel': 'Kichwa cha Hadithi',
    'submission.titlePlaceholder': 'Ingiza kichwa wazi kinachofafanua',
    'submission.era': 'Enzi ya Kihistoria',
    'submission.topic': 'Mada',
    'submission.region': 'Mkoa',
    'submission.certainty': 'Lebo ya Uhakika',
    'submission.body': 'Mwili wa Hadithi',
    'submission.anonymous': 'Wasilisha bila jina',
    'submission.saveDraft': 'Hifadhi Rasimu',
    'submission.submit': 'Wasilisha kwa Ukaguzi',
    'submission.submitting': 'Inawasilisha…',
    'submission.language': 'Lugha ya Hadithi',
    'submission.languageHint': 'Chagua lugha ambayo hadithi yako imeandikwa',
    // Certainty labels
    'certainty.oral_tradition': 'Mila ya Mdomo',
    'certainty.sourced_claim': 'Dai Lenye Chanzo',
    'certainty.firsthand_account': 'Ushuhuda wa Moja kwa Moja',
    // Reading
    'reading.wordCount': 'maneno',
    'reading.by': 'Na',
    'reading.shareContribution': 'Shiriki mchango huu',
  },
};

export function getTranslations(language: Language): (key: string) => string {
  const dict = translations[language] ?? translations.en;
  return (key: string) => dict[key] ?? translations.en[key] ?? key;
}
